import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { Trophy, Loader2, ChevronDown, ChevronUp, ExternalLink, Megaphone, RotateCcw } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    winner: '#FFB800', submitted: '#22D3EE', active: '#10B981',
    expired: '#94A3B8', failed: '#EF4444', disqualified: '#EF4444',
};

export default function AdminChallenges() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [deciding, setDeciding] = useState(null);
    const [autopost, setAutopost] = useState(true);
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/admin/engine/status`, { withCredentials: true })
            .then((r) => setAutopost(r.data.settings.leaderboard_autopost_enabled !== false))
            .catch(() => {});
    }, []);

    const toggleAutopost = async (v) => {
        setAutopost(v);
        try {
            await axios.put(`${API}/admin/engine/settings`, { leaderboard_autopost_enabled: v }, { withCredentials: true });
            toast.success(v ? 'Monthly leaderboard auto-post ON' : 'Monthly leaderboard auto-post OFF');
        } catch (e) {
            setAutopost(!v);
            toast.error('Failed to save setting');
        }
    };

    const postLeaderboard = async () => {
        setPosting(true);
        try {
            const res = await axios.post(`${API}/admin/challenges/leaderboard/post`, {}, { withCredentials: true });
            if (res.data.ok) toast.success('Leaderboard posted to the free channel! 📣');
            else toast.error(res.data.error || 'Failed to post');
        } catch (e) {
            toast.error('Failed to post');
        } finally {
            setPosting(false);
        }
    };

    const fetchAll = useCallback(async () => {
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const res = await axios.get(`${API}/admin/challenges`, { params, withCredentials: true });
            setItems(res.data);
        } catch (e) {
            toast.error('Failed to load challenges');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const decide = async (id, status) => {
        const note = status !== 'winner' ? window.prompt(`Reason for "${status}" (optional):`) : null;
        setDeciding(id);
        try {
            await axios.put(`${API}/admin/challenges/${id}/decide`, { status, note: note || null }, { withCredentials: true });
            toast.success(`Challenge marked as ${status}`);
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setDeciding(null);
        }
    };

    const resetTiming = async (id) => {
        if (!window.confirm('Restart this challenge? The 31-day clock and starting balance will reset to the current tier values.')) return;
        setDeciding(id);
        try {
            await axios.put(`${API}/admin/challenges/${id}/reset-timing`, {}, { withCredentials: true });
            toast.success('Challenge timer restarted ⏱️');
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setDeciding(null);
        }
    };

    const [resettingAll, setResettingAll] = useState(false);
    const resetAll = async () => {
        if (!window.confirm('Restart ALL active & expired challenges? Every participant gets a fresh 31-day clock and their starting balance resets to the current tier values.')) return;
        setResettingAll(true);
        try {
            const res = await axios.post(`${API}/admin/challenges/reset-all-timing`, {}, { withCredentials: true });
            toast.success(`Restarted ${res.data.reset_count} challenge timer(s) ⏱️`);
            await fetchAll();
        } catch (e) {
            toast.error('Failed to reset timers');
        } finally {
            setResettingAll(false);
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-challenges-page">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                        <Trophy className="w-7 h-7" style={{ color: '#FFB800' }} />
                        31-Day Challenge — Review
                    </h1>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-44" data-testid="challenge-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {['all', 'submitted', 'active', 'winner', 'expired', 'failed', 'disqualified'].map((s) => (
                                <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="card-surface p-4 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="leaderboard-broadcast-card">
                    <div className="flex items-center gap-3">
                        <Megaphone className="w-5 h-5" style={{ color: '#229ED9' }} />
                        <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Free-channel leaderboard broadcast</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Top 5 standings auto-post to your free Telegram channel once a month.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Label className="text-xs" style={{ color: 'var(--text-muted)' }}>Monthly auto-post</Label>
                            <Switch checked={autopost} onCheckedChange={toggleAutopost} data-testid="leaderboard-autopost-toggle" />
                        </div>
                        <Button size="sm" onClick={postLeaderboard} disabled={posting} data-testid="post-leaderboard-btn">
                            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Now 📣'}
                        </Button>
                    </div>
                </div>

                <div className="card-surface p-4 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="challenge-reset-card">
                    <div className="flex items-center gap-3">
                        <RotateCcw className="w-5 h-5" style={{ color: 'var(--primary-color)' }} />
                        <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Restart challenge timers</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Resets the 31-day clock + starting balance for all active & expired challenges — use after tier changes.</p>
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={resetAll} disabled={resettingAll} data-testid="reset-all-timers-btn">
                        {resettingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset All Timers</>}
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary-color)' }} /></div>
                ) : items.length === 0 ? (
                    <div className="card-surface p-10 text-center text-sm" style={{ color: 'var(--text-muted)' }} data-testid="no-challenges">
                        No challenges yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((c) => {
                            const sc = STATUS_COLORS[c.status] || '#94A3B8';
                            const open = expanded === c.id;
                            return (
                                <div key={c.id} className="card-surface" data-testid={`admin-challenge-${c.id}`}>
                                    <button type="button" className="w-full flex flex-wrap items-center justify-between gap-3 p-4 text-left" onClick={() => setExpanded(open ? null : c.id)} data-testid={`expand-challenge-${c.id}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{c.user_name}</span>
                                            <span className="text-xs px-2 py-0.5 rounded font-bold" style={{ backgroundColor: 'rgba(255,184,0,0.12)', color: '#FFB800' }}>{c.tier_label}</span>
                                            <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>${c.current_balance} / ${c.target} ({c.progress_pct}%)</span>
                                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Day {c.day}/31</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase" style={{ backgroundColor: `${sc}18`, color: sc, border: `1px solid ${sc}40` }}>{c.status}</span>
                                            {open ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                                        </div>
                                    </button>
                                    {open && (
                                        <div className="px-4 pb-4 space-y-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                            <div className="grid sm:grid-cols-2 gap-4 pt-4 text-sm">
                                                <div>
                                                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Timeline</p>
                                                    <p style={{ color: 'var(--text-secondary)' }}>Started: {c.started_at?.slice(0, 10)} · Ends: {c.ends_at?.slice(0, 10)}</p>
                                                    {c.submitted_at && <p style={{ color: 'var(--text-secondary)' }}>Submitted: {c.submitted_at?.slice(0, 16).replace('T', ' ')}</p>}
                                                </div>
                                                <div>
                                                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Balance updates ({(c.balance_updates || []).length})</p>
                                                    <div className="max-h-24 overflow-y-auto text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                                                        {(c.balance_updates || []).slice(-8).reverse().map((b, i) => (
                                                            <p key={i}>${b.balance} — {b.at?.slice(0, 16).replace('T', ' ')}</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            {c.submissions && (
                                                <div className="space-y-3">
                                                    <div className="flex flex-wrap gap-3">
                                                        <a href={`${API}/files/${c.submissions.notebook_video}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm underline" style={{ color: '#22D3EE' }} data-testid="notebook-video-link">
                                                            <ExternalLink className="w-3.5 h-3.5" /> Notebook video
                                                        </a>
                                                        <a href={`${API}/files/${c.submissions.account_video}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm underline" style={{ color: '#22D3EE' }} data-testid="account-video-link">
                                                            <ExternalLink className="w-3.5 h-3.5" /> Account video
                                                        </a>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Trade analyses ({c.submissions.analyses?.length})</p>
                                                        {(c.submissions.analyses || []).map((a, i) => (
                                                            <p key={i} className="text-sm mb-2 pl-3" style={{ color: 'var(--text-secondary)', borderLeft: '2px solid rgba(34,211,238,0.4)' }}>{a}</p>
                                                        ))}
                                                        {c.submissions.note && <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Note: {c.submissions.note}</p>}
                                                    </div>
                                                </div>
                                            )}
                                            {c.admin_note && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Admin note: {c.admin_note}</p>}
                                            {['submitted', 'active', 'expired'].includes(c.status) && (
                                                <div className="flex flex-wrap gap-2 pt-1">
                                                    <Button size="sm" onClick={() => decide(c.id, 'winner')} disabled={deciding === c.id} style={{ backgroundColor: '#FFB800', color: '#0B0F14' }} data-testid={`decide-winner-${c.id}`}>
                                                        🏆 Mark Winner
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => decide(c.id, 'failed')} disabled={deciding === c.id} data-testid={`decide-failed-${c.id}`}>
                                                        Mark Failed
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => decide(c.id, 'disqualified')} disabled={deciding === c.id} style={{ color: '#EF4444' }} data-testid={`decide-dq-${c.id}`}>
                                                        Disqualify
                                                    </Button>
                                                    {['active', 'expired'].includes(c.status) && (
                                                        <Button size="sm" variant="outline" onClick={() => resetTiming(c.id)} disabled={deciding === c.id} style={{ color: 'var(--primary-color)' }} data-testid={`reset-timing-${c.id}`}>
                                                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restart 31d
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}
